import { useState, useEffect, useRef } from "react";
import {
  Backdrop,
  Button,
  Chip,
  FormFieldSet,
  LogoLoader,
  showToast,
  useFormHandler,
} from "../../../../../components";
import { addTemplate, updateTemplate } from "../../../../../redux/slices/compaigns/compaignsApi";
import { useDispatch, useSelector } from "react-redux";
import { getTemplatesStart } from "../../../../../redux/slices/compaigns/compaignsSlice";

const SaveEmailTemplate = ({ onCancel, emailBody, setEmailBody, editTemplate }) => {
  // console.log("editTemplate",editTemplate)
  const [validationRules, setValidationRules] = useState({});
  const initialFormData = {
    title: editTemplate?.title || "",
    subject: editTemplate?.subject || "",
    // assignedTo: editTemplate?.assignedTo || "",
    assignedTo: editTemplate?.assignedTo?.firstName
      ? {
        label: `${editTemplate?.assignedTo?.firstName} ${editTemplate?.assignedTo?.lastName}`,
        value: editTemplate?.assignedTo?.uiid,
      }
      : "",
  };
  const { user } = useSelector((state) => state?.auth)
  const [activeInput, setActiveInput] = useState(null); // track last focused field
  const cursorPosRef = useRef({ start: 0, end: 0 });
  const dispatch = useDispatch();
  const { loading } = useSelector((state) => state?.compaigns) 
  const [saveLoading, setSaveLoading] = useState(false);
  let { formData, errors, handleChange, validateAllFields, setFormData } =
    useFormHandler(initialFormData, validationRules);

  const handleSelectChange = (value) => {
    setFormData({ ...formData, assignedTo: value });
  };

  const handleSubmit = async () => {
    const isValid = validateAllFields();
    if (!isValid) {
      return;
    }
    if (emailBody.trim() === "") {
      showToast("Email body cannot be empty", "error", 2000);
      return;
    }
    try {
      setSaveLoading(true);
      if (editTemplate?.uiid) {
        const payload = [
          {
            type: "SET",
            value: {
              title: formData.title,
              subject: formData.subject,
              body: emailBody,
              assignedTo: formData.assignedTo.value,
              // usedCount: usedCount || 0,
            },
          },
        ];
        const res = await updateTemplate({
          coll: "Template",
          uiid: editTemplate.uiid,
          payload,
        });
        if (res?.data) {
          onCancel();
          showToast("Template updated successfully", "success", 2000);
        } else {
          showToast(res?.error || "Failed to edit template", "error", 2000);
        }
      } else {
        const payload = {
          addedBy: user.data?.uiid,
          title: formData.title,
          subject: formData.subject,
          body: emailBody,
          assignedTo: formData.assignedTo.value,
        };

        const response = await addTemplate({ coll: "Template", payload });
        if (response?.data) {
          showToast("Template added successfully", "success", 2000);
          setEmailBody("");
          onCancel();

          dispatch(getTemplatesStart({ coll: "Template", lim: 5, pg: 1, ret: "uiid addedBy usedCount addedOn modified title", extret: "uiid firstName lastName email",sort: { addedOn: -1 }, }));
        } else {
          showToast(response?.error || "Failed to add template", "error", 2000);
        }
      }
    } catch (error) {
      console.error("Failed to add template:", error);
    } finally {
      setSaveLoading(false);
    }
  };

  useEffect(() => {
    const baseRules = {
      title: {
        type: "text",
        required: true,
      },
      subject: {
        type: "text",
        required: true,
      },
      // assignedTo: {
      //   type: "text",
      //   required: true,
      // },
      assignedTo: {
        type: Number,
        required: true,
      },
    };
    setValidationRules(baseRules);
  }, []);

  // const options = [
  //   { label: "Kevin", value: "Kevin" },
  //   { label: "John", value: "John" },
  //   { label: "Hammer", value: "Hammer" },
  //   { label: "George", value: "George" },
  //   { label: "Simon", value: "Simon" },
  //   { label: "Antony", value: "Antony" },
  //   { label: "Litton Das", value: "Litton Das" },
  //   { label: "Martin Screwer", value: "Martin Screwer" },
  // ];
  const options = [
    { label: "Kevin", value: 1 },
    { label: "John", value: 2 },
    { label: "Hammer", value: 3 },
    { label: "George", value: 4 },
    { label: "Simon", value: 5 },
    { label: "Antony", value: 6 },
    { label: "Litton Das", value: 7 },
    { label: "Martin Screwer", value: 8 },
  ];
  const insertAtCursor = (chipText) => {
    if (!activeInput) return;

    const { name } = activeInput.dataset;
    const { start, end } = cursorPosRef.current;
    const textToInsert = chipText + " ";
    let newVal = "";
    if (name === "subject") {
      newVal = formData.subject.substring(0, start) + textToInsert + formData.subject.substring(end);
      setFormData({ ...formData, subject: newVal });
    }

    if (name === "title") {
      newVal = formData.title.substring(0, start) + textToInsert + formData.title.substring(end);
      setFormData({ ...formData, title: newVal });
    }

    const newCursorPos = start + textToInsert.length;
    cursorPosRef.current = { start: newCursorPos, end: newCursorPos };

    setTimeout(() => {
      activeInput.setSelectionRange(newCursorPos, newCursorPos);
      activeInput.focus();
    }, 0);
  };

  useEffect(() => {
    const inputs = document.querySelectorAll("input[type='text']");
    inputs.forEach((input) => {
      const placeholder = input.getAttribute("placeholder") || "";
      if (placeholder.includes("Title")) {
        input.dataset.name = "title";
      } else if (placeholder.includes("Subject")) {
        input.dataset.name = "subject";
      }

      input.addEventListener("focus", () => setActiveInput(input));
      input.addEventListener("keyup", () => {
        cursorPosRef.current = {
          start: input.selectionStart,
          end: input.selectionEnd,
        };
      });
      input.addEventListener("click", () => {
        cursorPosRef.current = {
          start: input.selectionStart,
          end: input.selectionEnd,
        };
      });
    });
  }, [formData]);

  const generatePreview = () => {
    let preview = formData.subject;

    const replacements = {
      "${firstName}": "John",
      "${lastName}": "Doe",
      "${jobTitle}": "Web Developer",
      "${jobLocation}": "Hyderabad",
    };

    Object.keys(replacements).forEach((key) => {
      preview = preview.replaceAll(key, replacements[key]);
    });

    return preview;
  };

  // console.log(formData.assignedTo);

  return (
    <div className="dvh-100">
      <div className="p-3 grid grid-cols-2 gap-4">
        <div>
          <FormFieldSet
            title="Email Template Title"
            type="text"
            placeholder="Enter Email Template Title"
            value={formData.title}
            onChange={(value) => handleChange("title", value)}
            error={errors.title}
          />
        </div>
        <div className="col-span-2 flex flex-col">
          <FormFieldSet
            title="Mail Subject"
            type="text"
            placeholder="Enter Mail Subject"
            value={formData.subject}
            onChange={(value) => handleChange("subject", value)}
            error={errors.subject}
          />

          <div className="flex gap-1 mt-2 mb-5">
            <Chip
              label={"${firstName}"}
              variant="outlined"
              className="bg-gray-50 !text-gray-300 border !border-gray-200 !py-1 !px-2"
              onClick={() => insertAtCursor("${firstName}")}
            />
            <Chip
              label={"${lastName}"}
              variant="outlined"
              className="bg-gray-50 !text-gray-300 border !border-gray-200 !py-1 !px-2"
              onClick={() => insertAtCursor("${lastName}")}
            />
            <Chip
              label={"${jobTitle}"}
              variant="outlined"
              className="bg-gray-50 !text-gray-300 border !border-gray-200 !py-1 !px-2"
              onClick={() => insertAtCursor("${jobTitle}")}
            />
            <Chip
              label={"${jobLocation}"}
              variant="outlined"
              className="bg-gray-50 !text-gray-300 border !border-gray-200 !py-1 !px-2"
              onClick={() => insertAtCursor("${jobLocation}")}
            />
          </div>

          <h6 className="text-primary font-medium">
            {/* Web Developer in Hyderabad */}
            {generatePreview()}
          </h6>
        </div>
        <div>
          <FormFieldSet
            // type="select"
            type="AllBdmRecruiters"
            title="Assign To"
            placeholder="Select"
            // value={formData.assignedTo?.firstName + formData.assignedTo?.lastName}
            value={formData.assignedTo}
            onChange={handleSelectChange}
            error={errors.assignedTo?.firstName}
            showSearch
          />
        </div>
      </div>

      <div className="flex justify-center items-center gap-4 mt-25 mb-5">
        <Button
          title={"Cancel"}
          variant="outlined"
          color="black"
          onClick={onCancel}
        />
        <Button title={"Save Email Template"} disabled={loading} onClick={handleSubmit} />
      </div>
      <Backdrop isOpen={loading || saveLoading}>
        <LogoLoader />
      </Backdrop>
    </div>
  );
};

export default SaveEmailTemplate;
